import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Route, CheckCircle2, TrendingUp, Gift, Mail, Share2 } from "lucide-react";

export function CycleCompleteCard() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/cycle-recap").then((res) => setR(res.data)).catch(() => {}); }, []);
  if (!r) return null;

  const link = `${window.location.origin}?ref=${r.referral_code}`;
  const shareText = `Je progresse avec MAIRUNCOACH 🏃 Si tu veux te (re)lancer dans la course, utilise mon lien : ${link}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailto = `mailto:?subject=${encodeURIComponent("Rejoins-moi sur MAIRUNCOACH")}&body=${encodeURIComponent(shareText)}`;

  const hasVmaGain = (r.vma_delta ?? 0) > 0;
  const ca = r.cycle_analysis;

  return (
    <div className="mt-8 space-y-4" data-testid="cycle-complete-card">
      {/* Progrès */}
      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">
          <Trophy size={14} className="text-sage" /> Tes progrès sur ce cycle
        </div>
        <div className={`mt-4 grid ${hasVmaGain ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
          <div className="rounded-xl bg-secondary/50 p-4" data-testid="recap-km">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-obsidian/45"><Route size={13} /> Distance</div>
            <div className="mt-1.5 font-display text-2xl font-light tabnum">{r.distance_km_est != null ? `${r.distance_km_est}` : "—"}<span className="text-sm text-obsidian/40"> km</span></div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-4" data-testid="recap-sessions">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-obsidian/45"><CheckCircle2 size={13} /> Séances</div>
            <div className="mt-1.5 font-display text-2xl font-light tabnum">{r.sessions_done}</div>
          </div>
          {hasVmaGain && (
            <div className="rounded-xl bg-sage/12 p-4" data-testid="recap-vma">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-sage"><TrendingUp size={13} /> VMA</div>
              <div className="mt-1.5 font-display text-2xl font-light tabnum text-sage">+{r.vma_delta}<span className="text-sm"> km/h</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Bilan du cycle (analyse sur l'ensemble du cycle) */}
      {ca && (
        <div className="rounded-2xl border border-sage/30 bg-sage/8 p-6" data-testid="cycle-analysis">
          <div className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-semibold text-sage">
            <TrendingUp size={14} /> Bilan du cycle
          </div>
          <div className="mt-2 text-base font-medium text-obsidian">{ca.title}</div>
          <p className="mt-1 text-sm text-obsidian/75 leading-relaxed">{ca.message}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-full bg-white/70 border border-border px-2.5 py-1 text-obsidian/70">Assiduité {ca.adherence_pct}% · {ca.sessions_realized}/{ca.sessions_total}</span>
            {ca.avg_rpe != null && <span className="rounded-full bg-white/70 border border-border px-2.5 py-1 text-obsidian/70">RPE moyen {ca.avg_rpe}{ca.avg_rpe_target != null ? ` / ${ca.avg_rpe_target}` : ""}</span>}
            {ca.pain_count > 0 && <span className="rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-amber-700">{ca.pain_count} séance(s) avec douleur</span>}
          </div>
        </div>
      )}
      {r.referral_active && (
        <div className="rounded-2xl bg-gradient-to-br from-sage/12 via-brandblue/5 to-transparent border border-sage/25 p-6" data-testid="cycle-referral">
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center"><Gift size={18} className="text-sage" /></span>
            <div className="flex-1">
              <div className="text-base font-medium">Tu progresses bien 👏</div>
              <p className="mt-1 text-sm text-obsidian/65 leading-relaxed">
                Si tu connais quelqu'un qui voudrait se lancer, partage-lui ton lien — tout le monde y gagne :
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {r.referrer_percent ? (
                  <span data-testid="reward-referrer" className="inline-flex items-center gap-1.5 rounded-full bg-sage text-white text-xs font-semibold px-3 py-1.5">
                    <Gift size={13} /> Toi : -{r.referrer_percent}% sur ton prochain cycle
                  </span>
                ) : null}
                {r.referee_percent ? (
                  <span data-testid="reward-referee" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sage/40 text-obsidian/70 text-xs font-semibold px-3 py-1.5">
                    Ton ami : -{r.referee_percent}% sur son 1er cycle
                  </span>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <a data-testid="share-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <Share2 size={15} /> WhatsApp
                </a>
                <a data-testid="share-email" href={mailto}
                  className="inline-flex items-center gap-2 rounded-full border border-obsidian px-5 py-2.5 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">
                  <Mail size={15} /> E-mail
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CycleCompleteCard;
